import React, { useEffect, useState } from 'react';
import { LogOut, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface AccountProps {
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

export const Account: React.FC<AccountProps> = ({ setError, setSuccess }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<{ email: string | null; name: string | null }>({
    email: null,
    name: null
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Get the authenticated user
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Set the email from auth user data
          setUserData(prevData => ({
            ...prevData,
            email: user.email || null
          }));
          
          // Fetch the name from the profiles table
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('name')
            .eq('id', user.id)
            .single();
            
          if (profileError) {
            console.error('Error fetching profile data:', profileError);
          } else if (profileData) {
            setUserData(prevData => ({
              ...prevData,
              name: profileData.name
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };

    fetchUserData();
  }, []);

  const handleSignOut = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/auth');
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Error al cerrar sesión. Por favor, inténtalo nuevamente.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm mb-6">
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <User className="w-6 h-6 text-gray-700" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Cuenta</h2>
            <p className="text-sm text-gray-500">
              Gestiona tu cuenta de usuario
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <div className="space-y-6">
          <div className="p-4 bg-gray-50 border border-gray-100 rounded-md mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-md font-medium text-gray-900">
                  {userData.email || 'Cargando...'}
                </h3>
                <p className="text-sm text-gray-600">
                  Correo electrónico de tu cuenta
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 border border-gray-100 rounded-md">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-md font-medium text-gray-900">
                  {userData.name || 'No disponible'}
                </h3>
                <p className="text-sm text-gray-600">
                  Nombre de usuario
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t pt-6">
            <h3 className="text-md font-medium text-gray-900 mb-4">
              Sesión
            </h3>
            <button
              onClick={handleSignOut}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
